import React, { useEffect, useRef, useState } from 'react';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';
import { useParams, useNavigate } from 'react-router-dom';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';

const API = process.env.REACT_APP_API_URL || 'https://agents.matix.one';

// Contract data placeholders the user can insert
const WO_FIELDS = [
  { id: 'wo_number', label: 'WO Number', label_ar: 'رقم أمر العمل' },
  { id: 'title', label: 'Title', label_ar: 'العنوان' },
  { id: 'title_ar', label: 'Title (AR)', label_ar: 'العنوان (عربي)' },
  { id: 'description', label: 'Description', label_ar: 'الوصف' },
  { id: 'wo_type', label: 'WO Type', label_ar: 'نوع أمر العمل' },
  { id: 'priority', label: 'Priority', label_ar: 'الأولوية' },
  { id: 'status', label: 'Status', label_ar: 'الحالة' },
  { id: 'source', label: 'Source', label_ar: 'المصدر' },
  { id: 'scheduled_date', label: 'Scheduled Date', label_ar: 'تاريخ الجدولة' },
  { id: 'scheduled_time', label: 'Scheduled Time', label_ar: 'وقت الجدولة' },
  { id: 'actual_start', label: 'Actual Start', label_ar: 'البداية الفعلية' },
  { id: 'actual_end', label: 'Actual End', label_ar: 'النهاية الفعلية' },
  { id: 'completed_at', label: 'Completed At', label_ar: 'تاريخ الإنجاز' },
  { id: 'resolution_summary', label: 'Resolution', label_ar: 'ملخص الحل' },
  { id: 'notes', label: 'Notes', label_ar: 'ملاحظات' },
  { id: 'total_labor_cost', label: 'Labor Cost', label_ar: 'تكلفة العمالة' },
  { id: 'total_parts_cost', label: 'Parts Cost', label_ar: 'تكلفة القطع' },
  { id: 'total_cost', label: 'Total Cost', label_ar: 'التكلفة الإجمالية' },
  { id: 'is_billable', label: 'Billable', label_ar: 'قابل للفوترة' },
  { id: 'client_name', label: 'Client Name', label_ar: 'اسم العميل' },
  { id: 'client_phone', label: 'Client Phone', label_ar: 'هاتف العميل' },
  { id: 'client_email', label: 'Client Email', label_ar: 'بريد العميل' },
  { id: 'client_address', label: 'Client Address', label_ar: 'عنوان العميل' },
  { id: 'equipment_name', label: 'Equipment', label_ar: 'المعدة' },
  { id: 'equipment_serial', label: 'Serial #', label_ar: 'الرقم التسلسلي' },
  { id: 'equipment_model', label: 'Model', label_ar: 'الموديل' },
  { id: 'equipment_brand', label: 'Brand', label_ar: 'الماركة' },
  { id: 'equipment_category', label: 'Category', label_ar: 'الفئة' },
  { id: 'equipment_location', label: 'Location', label_ar: 'الموقع' },
  { id: 'technician_name', label: 'Technician', label_ar: 'الفني' },
  { id: 'technician_phone', label: 'Tech Phone', label_ar: 'هاتف الفني' },
  { id: 'contract_number', label: 'Contract #', label_ar: 'رقم العقد' },
  { id: 'org_name', label: 'Organization', label_ar: 'المنظمة' },
  { id: 'today_date', label: "Today's Date", label_ar: 'تاريخ اليوم' },
  { id: 'time_logs_table', label: 'Time Logs Table', label_ar: 'جدول الوقت' },
  { id: 'parts_table', label: 'Parts Table', label_ar: 'جدول القطع' },
  { id: 'signature_image', label: 'Customer Signature', label_ar: 'توقيع العميل' },
];

// Default contract template HTML
const DEFAULT_TEMPLATE = `<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Cairo', 'Segoe UI', Tahoma, Arial, sans-serif; font-size: 11pt; color: #333; line-height: 1.5; }
  .wo-page { max-width: 210mm; margin: 0 auto; padding: 15mm; background: white; }
  .header { text-align: center; margin-bottom: 15px; border-bottom: 3px solid #1a2038; padding-bottom: 12px; }
  .header h1 { font-size: 20pt; color: #1a2038; margin-bottom: 2px; }
  .header h3 { font-size: 11pt; color: #007bff; font-weight: normal; }
  .header .wo-no { font-size: 14pt; font-weight: bold; color: #1a2038; margin-top: 4px; }
  .section-title { font-size: 12pt; font-weight: bold; color: #1a2038; margin: 14px 0 8px; padding: 4px 8px; background: #f0f2f5; border-left: 4px solid #007bff; }
  .field-grid { display: flex; flex-wrap: wrap; gap: 6px 20px; }
  .field-item { width: calc(50% - 10px); display: flex; margin-bottom: 4px; }
  .field-label { width: 140px; color: #888; font-size: 9pt; flex-shrink: 0; }
  .field-value { font-weight: 600; font-size: 9pt; }
  .divider { border: none; border-top: 1px solid #ddd; margin: 12px 0; }
  .info-table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 9pt; }
  .info-table th { background: #1a2038; color: white; padding: 6px 10px; text-align: left; }
  .info-table td { padding: 6px 10px; border: 1px solid #ddd; }
  .info-table tr:nth-child(even) { background: #f9f9f9; }
  .cost-box { background: #f8f9fa; border: 1px solid #ddd; border-radius: 6px; padding: 12px; margin: 8px 0; }
  .cost-row { display: flex; justify-content: space-between; padding: 3px 0; font-size: 10pt; }
  .cost-row.total { border-top: 2px solid #1a2038; margin-top: 6px; padding-top: 6px; font-weight: bold; font-size: 12pt; color: #007bff; }
  .signatures { display: flex; justify-content: space-between; margin-top: 30px; }
  .sig-block { text-align: center; min-width: 180px; }
  .sig-line { border-bottom: 1px solid #333; width: 180px; margin: 0 auto 6px; height: 35px; }
  .sig-label { font-weight: bold; font-size: 9pt; }
  .sig-date { color: #888; font-size: 8pt; margin-top: 3px; }
  .footer { text-align: center; font-size: 8pt; color: #999; margin-top: 20px; border-top: 1px solid #ddd; padding-top: 8px; }
  .placeholder { background: #e8f0fe; border: 1px dashed #4285f4; padding: 1px 5px; border-radius: 3px; color: #1a73e8; font-size: inherit; }
  @media print { .wo-page { padding: 10mm; max-width: none; } body { -webkit-print-color-adjust: exact; } }
</style>
<div class="wo-page">
  <div class="header"><h1>\${'{{'+'org_name'+'}}'}</h1><h3>WORK ORDER REPORT</h3><div class="wo-no">\${'{{'+'wo_number'+'}}'}</div></div>
  <div class="section-title">Work Order Details</div>
  <div class="field-grid">
    <div class="field-item"><span class="field-label">Title:</span> <span class="field-value">\${'{{'+'title'+'}}'}</span></div>
    <div class="field-item"><span class="field-label">Type:</span> <span class="field-value">\${'{{'+'wo_type'+'}}'}</span></div>
    <div class="field-item"><span class="field-label">Priority:</span> <span class="field-value">\${'{{'+'priority'+'}}'}</span></div>
    <div class="field-item"><span class="field-label">Status:</span> <span class="field-value">\${'{{'+'status'+'}}'}</span></div>
  </div>
  <div class="footer">Generated on \${'{{'+'today_date'+'}}'} | \${'{{'+'wo_number'+'}}'} | \${'{{'+'org_name'+'}}'}</div>
</div>`;

const WOTemplateBuilder = () => {
  const { lang, t } = useLanguage();
  const isRTL = lang === 'ar';
  const { id } = useParams();
  const navigate = useNavigate();

  const editorRef = useRef(null);
  const containerRef = useRef(null);
  const [templateMeta, setTemplateMeta] = useState({
    name: '', name_ar: '', language: 'en', is_default: false
  });
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);

  const inputSt = {
    width: '100%', padding: '10px',
    border: '2px solid rgba(255,255,255,0.2)',
    borderRadius: '8px', backgroundColor: '#1a2038',
    color: '#f9fafb', fontSize: '13px', outline: 'none', boxSizing: 'border-box'
  };
  const labelSt = { display: 'block', marginBottom: '6px', color: '#d1d5db', fontSize: '12px', fontWeight: '500' };

  useEffect(() => {
    let destroyed = false;

    const init = async () => {
      await new Promise(r => setTimeout(r, 300));
      if (destroyed || !containerRef.current) return;

      let htmlContent = DEFAULT_TEMPLATE;
      let cssContent = '';

      // Load existing template if editing
      if (id) {
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API}/api/v1/fsm/wo-templates/${id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await res.json();
          if (data.success && data.data) {
            const tmpl = data.data;
            setTemplateMeta({
              name: tmpl.name || '', name_ar: tmpl.name_ar || '',
              language: tmpl.language || 'en', is_default: !!tmpl.is_default
            });

            const stored = typeof tmpl.schema === 'string' ? JSON.parse(tmpl.schema) : tmpl.schema;
            if (stored && stored.html) {
              htmlContent = stored.html;
              cssContent = stored.css || '';
            } else if (stored && typeof stored === 'string') {
              htmlContent = stored;
            }
          }
        } catch (err) {
          console.error('Error loading template:', err);
        }
      }

      if (destroyed || !containerRef.current) return;

      // Destroy old editor
      if (editorRef.current) {
        try { editorRef.current.destroy(); } catch (e) {}
      }

      // Initialize GrapesJS
      const editor = grapesjs.init({
      assetManager: {
        upload: false,
        custom: {
          open(props) {
            // Create a hidden file input
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.multiple = true;
            input.onchange = async (e) => {
              const files = e.target.files;
              if (!files || files.length === 0) return;
              const token = localStorage.getItem('token');
              for (let i = 0; i < files.length; i++) {
                const formData = new FormData();
                formData.append('file', files[i]);
                try {
                  const res = await fetch('/api/v1/fsm/wo-templates/upload-asset', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: formData,
                  });
                  const data = await res.json();
                  if (data.success && data.url) {
                    props.select({ src: data.url, type: 'image' });
                  }
                } catch (err) {
                  console.error('Asset upload failed:', err);
                }
              }
            };
            input.click();
          },
          close() {},
        },
      },
        container: containerRef.current,
        height: '100%',
        width: 'auto',
        fromElement: false,
        components: htmlContent,
        style: cssContent,
        storageManager: false,
        panels: { defaults: [] },
        deviceManager: {
          devices: [
            { name: 'A4 Portrait', width: '210mm' },
            { name: 'A4 Landscape', width: '297mm' },
            { name: 'Letter', width: '8.5in' },
          ]
        },
        canvas: {
          styles: [
            'https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&family=Amiri:wght@400;700&display=swap',
          ],
        },
        plugins: [],
        pluginsOpts: {},
      });

      // Add custom blocks for contract fields
      const bm = editor.BlockManager;

      // Section blocks
      bm.add('section-heading', {
        label: '📋 Section Title',
        category: 'Layout',
        content: '<div class="section-title">Section Title</div>',
      });

      bm.add('divider', {
        label: '➖ Divider',
        category: 'Layout',
        content: '<hr class="divider-light" />',
      });

      bm.add('divider-bold', {
        label: '➖ Bold Divider',
        category: 'Layout',
        content: '<hr class="divider" />',
      });

      bm.add('two-columns', {
        label: '📐 Two Columns',
        category: 'Layout',
        content: '<div class="two-col"><div><p>Column 1</p></div><div><p>Column 2</p></div></div>',
      });

      bm.add('three-columns', {
        label: '📐 Three Columns',
        category: 'Layout',
        content: '<div style="display:flex;gap:15px;"><div style="flex:1;"><p>Col 1</p></div><div style="flex:1;"><p>Col 2</p></div><div style="flex:1;"><p>Col 3</p></div></div>',
      });

      bm.add('text-block', {
        label: '📝 Text Block',
        category: 'Content',
        content: '<p class="terms-text">Enter your text here. You can use {{field_name}} placeholders for dynamic contract data.</p>',
      });

      bm.add('heading', {
        label: '📰 Heading',
        category: 'Content',
        content: '<h2 style="font-size:18pt; color:#1a2038; margin:10px 0;">Heading Text</h2>',
      });

      bm.add('image-block', {
        label: '🖼️ Image/Logo',
        category: 'Content',
        content: '<div style="text-align:center; margin:10px 0;"><img src="https://via.placeholder.com/200x80?text=Your+Logo" style="max-width:200px; max-height:80px;" /></div>',
      });

      bm.add('spacer', {
        label: '↕️ Spacer',
        category: 'Layout',
        content: '<div style="height:20px;"></div>',
      });

      // Signature block
      bm.add('signatures', {
        label: '✒️ Signatures',
        category: 'Content',
        content: `<div class="signatures">
          <div class="sig-block"><div class="sig-line"></div><div class="sig-label">Signature 1</div><div class="sig-date">Date: _______________</div></div>
          <div class="sig-block"><div class="sig-line"></div><div class="sig-label">Signature 2</div><div class="sig-date">Date: _______________</div></div>
        </div>`,
      });

      // Data table
      bm.add('data-table', {
        label: '📊 Data Table',
        category: 'Content',
        content: `<table class="info-table">
          <tr><th>Column 1</th><th>Column 2</th><th>Column 3</th></tr>
          <tr><td>Data</td><td>Data</td><td>Data</td></tr>
          <tr><td>Data</td><td>Data</td><td>Data</td></tr>
        </table>`,
      });

      // Field row (label + value)
      bm.add('field-row', {
        label: '📋 Field Row',
        category: 'Content',
        content: '<div class="field-row"><span class="field-label">Label:</span> <span class="field-value">{{field_name}}</span></div>',
      });

      // Contract field placeholder blocks
      WO_FIELDS.forEach(field => {
        if (field.id === 'time_logs_table') {
          bm.add('field-time_logs_table', { label: 'Time Logs Table', category: 'WO Fields', content: '<div>{{time_logs_table}}</div>' });
        } else if (field.id === 'parts_table') {
          bm.add('field-parts_table', { label: 'Parts Table', category: 'WO Fields', content: '<div>{{parts_table}}</div>' });
        } else if (field.id === 'signature_image') {
          bm.add('field-signature_image', { label: 'Customer Signature', category: 'WO Fields', content: '<div>{{signature_image}}</div>' });
        } else {
          bm.add(`field-${field.id}`, {
            label: `📌 ${field.label}`,
            category: 'WO Fields',
            content: `<span class="placeholder">{{${field.id}}}</span>`,
          });
        }
      });

      // Add custom panels
      const pn = editor.Panels;

      // Toolbar buttons
      pn.addPanel({
        id: 'views',
        buttons: [
          { id: 'open-blocks', active: true, label: 'Blocks', command: 'open-blocks', togglable: false },
          { id: 'open-sm', label: 'Styles', command: 'open-sm', togglable: false },
          { id: 'open-layers', label: 'Layers', command: 'open-layers', togglable: false },
        ],
      });

      editorRef.current = editor;
      setReady(true);
      console.log('GrapesJS editor initialized');
    };

    init();

    return () => {
      destroyed = true;
      if (editorRef.current) {
        try { editorRef.current.destroy(); } catch (e) {}
      }
    };
  }, [id]);

  // Save template
  const handleSave = async () => {
    if (!templateMeta.name) {
      alert('Template name is required');
      return;
    }
    if (!editorRef.current) return;

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const editor = editorRef.current;

      const schema = {
        html: editor.getHtml(),
        css: editor.getCss(),
        components: JSON.stringify(editor.getComponents()),
        styles: JSON.stringify(editor.getStyle()),
      };

      const payload = {
        name: templateMeta.name,
        name_ar: templateMeta.name_ar,
        language: templateMeta.language,
        is_default: templateMeta.is_default,
        schema,
      };

      const url = id
        ? `${API}/api/v1/fsm/wo-templates/${id}`
        : `${API}/api/v1/fsm/wo-templates`;

      const res = await fetch(url, {
        method: id ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.success) {
        if (!id && data.data && data.data.id) {
          navigate(`/fsm/wo-templates/${data.data.id}/edit`, { replace: true });
        }
        alert('Template saved!');
      } else {
        alert(data.message || 'Error saving');
      }
    } catch (err) {
      console.error(err);
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Preview / Print PDF
  const handlePreviewPDF = () => {
    if (!editorRef.current) return;

    const editor = editorRef.current;
    const html = editor.getHtml();
    const css = editor.getCss();

    const fullHTML = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&family=Amiri:wght@400;700&display=swap" rel="stylesheet">
  <style>
    ${css}
    @page { size: A4; margin: 0; }
    @media print { body { -webkit-print-color-adjust: exact; } }
  </style>
</head>
<body>${html}</body>
</html>`;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(fullHTML);
    printWindow.document.close();
    // Auto print after fonts load
    printWindow.onload = () => {
      setTimeout(() => printWindow.print(), 500);
    };
  };

  return (
    <Layout>
      <div style={{ direction: isRTL ? 'rtl' : 'ltr', color: '#f9fafb', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)' }}>

        {/* Top Bar */}
        <div style={{
          display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap',
          padding: '12px 16px',
          backgroundColor: 'rgba(26, 32, 56, 0.9)',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          zIndex: 10
        }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <label style={labelSt}>{isRTL ? 'اسم القالب *' : 'Template Name *'}</label>
            <input type="text" value={templateMeta.name}
              onChange={(e) => setTemplateMeta(prev => ({ ...prev, name: e.target.value }))}
              style={inputSt} placeholder={isRTL ? 'مثال: أمر عمل قياسي' : 'e.g. Standard Work Order'} />
          </div>
          <div style={{ minWidth: '100px' }}>
            <label style={labelSt}>{isRTL ? 'الاسم (عربي)' : 'Name (AR)'}</label>
            <input type="text" value={templateMeta.name_ar}
              onChange={(e) => setTemplateMeta(prev => ({ ...prev, name_ar: e.target.value }))}
              style={{ ...inputSt, direction: 'rtl' }} />
          </div>
          <div style={{ minWidth: '100px' }}>
            <label style={labelSt}>{isRTL ? 'اللغة' : 'Language'}</label>
            <select value={templateMeta.language}
              onChange={(e) => setTemplateMeta(prev => ({ ...prev, language: e.target.value }))}
              style={inputSt}>
              <option value="en">English</option>
              <option value="ar">العربية</option>
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#d1d5db', fontSize: '12px', paddingBottom: '10px' }}>
            <input type="checkbox" checked={templateMeta.is_default}
              onChange={(e) => setTemplateMeta(prev => ({ ...prev, is_default: e.target.checked }))}
              style={{ accentColor: '#007bff' }} />
            Default
          </label>
          <div style={{ display: 'flex', gap: '8px', paddingBottom: '2px' }}>
            <button onClick={handleSave} disabled={saving}
              style={{
                backgroundColor: saving ? '#6c757d' : '#007bff', color: 'white', border: 'none',
                borderRadius: '8px', padding: '10px 20px', cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '14px', fontWeight: '600'
              }}>
              {saving ? (isRTL ? 'جاري الحفظ...' : 'Saving...') : (isRTL ? 'حفظ' : 'Save')}
            </button>
            <button onClick={handlePreviewPDF}
              style={{
                backgroundColor: 'transparent', color: '#4ade80',
                border: '2px solid rgba(74,222,128,0.4)', borderRadius: '8px',
                padding: '10px 16px', cursor: 'pointer', fontSize: '14px'
              }}>
              Preview / Print PDF
            </button>
            <button onClick={() => navigate('/fsm/wo-templates')}
              style={{
                backgroundColor: 'transparent', color: '#d1d5db',
                border: '2px solid rgba(255,255,255,0.2)', borderRadius: '8px',
                padding: '10px 16px', cursor: 'pointer', fontSize: '14px'
              }}>
              Back
            </button>
          </div>
        </div>

        {/* GrapesJS Editor Container */}
        <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          {!ready && (
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(21,27,48,0.9)', zIndex: 10, color: '#9ca3af' }}>
              Loading editor...
            </div>
          )}
          <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
        </div>

      </div>

      {/* Override GrapesJS styles for dark theme compatibility */}
      <style>{`
        .gjs-one-bg { background-color: #1a2038 !important; }
        .gjs-two-color { color: #d1d5db !important; }
        .gjs-three-bg { background-color: #151b30 !important; }
        .gjs-four-color, .gjs-four-color-h:hover { color: #60a5fa !important; }
        .gjs-pn-views, .gjs-pn-views-container { background-color: #1a2038 !important; }
        .gjs-block { background-color: rgba(255,255,255,0.05) !important; border: 1px solid rgba(255,255,255,0.1) !important; border-radius: 8px !important; color: #d1d5db !important; min-height: auto !important; }
        .gjs-block:hover { border-color: rgba(0,123,255,0.5) !important; }
        .gjs-block__media { display: none !important; }
        .gjs-blocks-cs { padding: 5px !important; }
        .gjs-category-title, .gjs-layer-title, .gjs-sm-sector-title { background-color: rgba(255,255,255,0.05) !important; border-color: rgba(255,255,255,0.1) !important; color: #d1d5db !important; }
        .gjs-sm-label, .gjs-field { color: #d1d5db !important; }
        .gjs-field input, .gjs-field select, .gjs-field textarea { color: #f9fafb !important; background: #1a2038 !important; }
        .gjs-pn-btn { color: #9ca3af !important; }
        .gjs-pn-btn.gjs-pn-active { color: #60a5fa !important; }
        .gjs-cv-canvas { background-color: #2a2f45 !important; }
      `}</style>
    </Layout>
  );
};

export default WOTemplateBuilder;